import type { SerializedError } from '@reduxjs/toolkit';

import axios from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import initialConfig from '../initialConfig';

const initialState: {
  anonymous_activitiesData: any;
  anonymous_activitiesLoading: boolean;
  anonymous_activitiesError: SerializedError | null;
  page: number;
} = {
  anonymous_activitiesData: {},
  anonymous_activitiesLoading: false,
  anonymous_activitiesError: null,
  page: 1,
};

export interface IAnonymousActivities {
  sort?: string;
  fee: string | null;
  fromQuota: number | null;
  toQuota: number | null;
  byCoordinates: boolean | null;
  long: number | null;
  lat: number | null;
  finished: boolean | null;
  page: number | null;
  pageSize: number | null;
  listing: {
    title: string | null;
    ageInterval: number[] | null;
    format: string | null;
    category: string | null;
  };
  location: {
    district: string | null;
    city: string | null;
    parking: string | null;
  };
  schedule: {
    days: number[] | null;
    dateInterval: {
      from: string | null;
      to: string | null;
    };
  };
  price: {
    from: number | null;
    to: number | null;
  };
}

export const anonymous_activitiesFunc = createAsyncThunk(
  'anonymous_activities/anonymous_activitiesFunc',
  async (values: IAnonymousActivities) => {
    const data1 = JSON.stringify({
      query: `query Anonymous_activities($filter: FilterActivitiesByAnonymousInput, $pagination: Pagination, $otherSortings: CategoryDefaultSorting) {
      anonymous_activities(filter: $filter, pagination: $pagination, otherSortings: $otherSortings) {
        count
        data {
          _id
          additionalDetails
          createdAt
          duration
          fee
          hoursBeforeReservationExpires
        externalLink
        externalMail
        externalPhoneNumber
         isReservationRequired
          listing {
            _id
            ageInterval
            category
            companionship
            coverImage
            description
            exteriorOrganizer {
              _id
              name
              imageUrl
              details
              createdAt
              updatedAt
              socialMedia {
                website
                instagram
                twitter
                facebook
                youtube
              }
            }
            format
            images
            rating {
              average
              numberOfReviews
              createdAt
              updatedAt
            }
            subCategory
            title
          }
          location {
            _id
            city
            district
            country
            address {
              description
              title
            }
            lat
            long
            parking
          }
          price
          quota
          schedule {
            _id
            createdAt
            days {
              _id
              activity {
                _id
              }
              date
              day
              startTime
              endTime
              createdAt
              updatedAt
            }
            endDate
            repeatedIntervalType
            startDate
            totalDayCount
            updatedAt
          }
          status
          updatedAt
          updatedBy {
            _id
            displayName
            displayImageUrl
            description
            role 
          }
          title
          participantCount
          createdBy {
            _id
            displayName
            displayImageUrl
            description
            role
          }
        }
      }
    }`,
      variables: {
        filter: {
          additionalDetails: null,
          duration: {
            from: null,
            to: null,
          },
          fee: values?.fee ?? null,
          listing: {
            ageInterval: values?.listing?.ageInterval ?? null,
            category: values?.listing?.category ?? null,
            companionship: null,
            description: null,
            format: values?.listing?.format ?? null,
            title: values?.listing?.title ?? null,
          },
          price: {
            from: values?.price?.from ?? null,
            to: values?.price?.to ?? null,
          },
          quota: {
            from: values?.fromQuota ?? null,
            to: values?.toQuota ?? null,
          },
          location: {
            city: values?.location?.city ?? null,
            district: values?.location?.district ?? null,
            parking: values?.location?.parking ?? null,
            byCoordinates:
              values?.byCoordinates === false
                ? null
                : {
                    radius: null,
                    long: values?.long ?? null,
                    lat: values?.lat ?? null,
                  },
          },
          schedule: {
            finished: false,
            days: values?.schedule?.days ?? null,
            dateInterval: {
              from: values?.schedule?.dateInterval?.from ?? null,
              to: values?.schedule?.dateInterval?.to ?? null,
            },
          },
        },
        pagination: {
          page: values.page,
          pageSize: values.pageSize,
        },
        otherSortings: values.sort,
      },
    });

    const config = initialConfig(process.env.REACT_APP_BASE_URL_ACTIVITY || '', data1, '');

    const response = axios(config)
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((err) => err);

    const data = await response;
    return data;
  }
);

const anonymous_activities = createSlice({
  name: 'anonymous_activities',
  initialState,
  reducers: {
    setPaginationAnonymous: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(anonymous_activitiesFunc.pending, (state) => {
      state.anonymous_activitiesLoading = true;
    });
    builder.addCase(anonymous_activitiesFunc.fulfilled, (state, action) => {
      state.anonymous_activitiesLoading = false;
      state.anonymous_activitiesData = action.payload?.data?.anonymous_activities;
      state.anonymous_activitiesError = action.payload.errors || action.payload?.response?.status;
    });
    builder.addCase(anonymous_activitiesFunc.rejected, (state, action) => {
      state.anonymous_activitiesLoading = false;
      state.anonymous_activitiesError = action.error;
    });
  },
});

export const { setPaginationAnonymous } = anonymous_activities.actions;
export default anonymous_activities.reducer;
